import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../../components/common/Logo';
import bg from '../../assets/bg-register.jpg';
import { Loader2 } from 'lucide-react';
import { sendPasswordResetEmail } from '../../services/Firebase/Login';
import { Capacitor } from '@capacitor/core';
import type { Auth } from 'firebase/auth';

export const RecoverPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate email
      if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      // Get Firebase Auth instance for web
      let webAuth: Auth | undefined;
      if (!Capacitor.isNativePlatform()) {
        const firebaseModule = await import('../../config/firebase');
        webAuth = firebaseModule.auth || undefined;
      }

      // Send password reset email using Firebase
      await sendPasswordResetEmail(email, webAuth);
      
      setSuccess(true);
    } catch (err: any) {
      console.error('Password recovery failed:', err);
      
      // Handle specific Firebase error codes
      let errorMessage = 'Failed to send recovery email. Please try again.';
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="h-screen bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="h-screen flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8">
          {/* Logo section above titles */}
          <div className="flex justify-center mb-8">
            <div className="scale-150">
              <Logo />
            </div>
          </div>
          
          <div className="space-y-4 text-center">
            <div className="space-y-2">
              <h2 className="text-3xl sm:text-4xl text-white font-bold leading-snug tracking-tight text-center">
                Forgot Your Password?
              </h2>
              <p className="text-lg text-gray-300 font-montserrat font-medium">
                Enter your email and<br /> we'll send a link to reset it.
              </p>
            </div>
          </div>
  
          {success ? (
            <div className="space-y-6 text-center">
              <div className="bg-gradient-to-br from-[#79c9cb]/10 to-[#4b7bdd]/10 text-[#79c9cb] p-4 rounded-lg">
                <p className="font-semibold mb-2">Password reset email sent!</p>
                <p className="text-sm">
                  Check your inbox at <strong>{email}</strong> for instructions to reset your password.
                </p>
              </div>
              <Link
                to="/login"
                className="block w-full px-4 py-3 bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] text-gray-900 rounded-lg font-semibold hover:opacity-90 transition-opacity text-center"
              >
                Return to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email..."
                className="input-field"
                required
                disabled={isLoading}
              />
  
              {error && (
                <p className="text-red-500 text-sm text-center">{error}</p>
              )}
  
              <div className="space-y-3">
                <button
                  type="submit"
                  className="w-full px-4 py-3 bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] text-gray-900 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
  
                <Link
                  to="/login"
                  className="block text-center text-sm text-[#5CD2F4] hover:underline"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );  
};